<template>
  <v-card :loading="loading">
    <v-card-title>
      Billing Info
      <v-spacer></v-spacer>
      <img src="../../assets/images/powered-by-stripe.svg" alt="" />
    </v-card-title>
    <v-simple-table>
      <template v-slot:default>
        <tbody v-if="billingInfo">
          <tr>
            <td style="width: 300px">Customer Stripe ID</td>
            <td>
              <span ref="stripeCustomerId">
                {{ billingInfo.stripeCustomerId }}
              </span>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    class="ma-2"
                    text
                    icon
                    v-if="billingInfo.stripeCustomerId"
                    @click.prevent="
                      copyStripeCustomerId(billingInfo.stripeCustomerId)
                    "
                  >
                    <img
                      svg-inline
                      src="../../assets/images/ico-copy.svg"
                      alt=""
                    />
                  </v-btn>
                </template>
                <span>Copy</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    class="ma-2"
                    text
                    icon
                    color="primary"
                    :href="stripeDashboardUrl"
                    target="_blank"
                  >
                    <v-icon>link</v-icon>
                  </v-btn>
                </template>
                <span>Open in Stripe 🔗</span>
              </v-tooltip>
            </td>
          </tr>
          <tr>
            <td>Customer Name</td>
            <td>{{ billingInfo.stripeCustomer.name }}</td>
          </tr>
          <template v-if="billingInfo.stripePaymentMethod">
            <tr>
              <td>Payment Method</td>
              <td>{{ billingInfo.stripePaymentMethod.type }}</td>
            </tr>
            <template
              v-if="billingInfo.stripePaymentMethod.type === 'sepa_debit'"
            >
              <tr>
                <td>IBAN</td>
                <td>
                  XXXXXXXXXXXXXXXXXX{{
                    billingInfo.stripePaymentMethod.sepa_debit.last4
                  }}
                </td>
              </tr>
            </template>
            <template
              v-else-if="billingInfo.stripePaymentMethod.type === 'card'"
            >
              <tr>
                <td>Card Type</td>
                <td>{{ billingInfo.stripePaymentMethod.card.brand }}</td>
              </tr>
              <tr>
                <td>Card Number</td>
                <td>
                  XXXX XXXX XXXX
                  {{ billingInfo.stripePaymentMethod.card.last4 }}
                </td>
              </tr>
              <tr>
                <td>Card Expires</td>
                <td>
                  {{ billingInfo.stripePaymentMethod.card.exp_month }}/{{
                    billingInfo.stripePaymentMethod.card.exp_year
                  }}
                </td>
              </tr>
            </template>
            <template
              v-else-if="billingInfo.stripePaymentMethod.type === 'paypal'"
            >
              <tr>
                <td>Paypal Email</td>
                <td>
                  <span ref="paypalEmail">
                    {{ billingInfo.stripePaymentMethod.paypal.payer_email }}
                  </span>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        class="ma-2"
                        text
                        icon
                        v-if="
                          billingInfo.stripePaymentMethod.paypal.payer_email
                        "
                        @click.prevent="
                          copyPaypalEmail(
                            billingInfo.stripePaymentMethod.paypal.payer_email
                          )
                        "
                      >
                        <img
                          svg-inline
                          src="../../assets/images/ico-copy.svg"
                          alt=""
                        />
                      </v-btn>
                    </template>
                    <span>Copy</span>
                  </v-tooltip>
                </td>
              </tr>
            </template>
          </template>
          <tr>
            <td>Currency</td>
            <td>{{ billingInfo.stripeCustomer.currency }}</td>
          </tr>
          <tr>
            <td>Billing Email</td>
            <td>
              <span ref="billingEmail">
                {{ billingInfo.stripeCustomer.metadata.billing_email }}
              </span>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    class="ma-2"
                    text
                    icon
                    v-if="billingInfo.stripeCustomer.metadata.billing_email"
                    @click.prevent="
                      copyBillingEmail(
                        billingInfo.stripeCustomer.metadata.billing_email
                      )
                    "
                  >
                    <img
                      svg-inline
                      src="../../assets/images/ico-copy.svg"
                      alt=""
                    />
                  </v-btn>
                </template>
                <span>Copy</span>
              </v-tooltip>
            </td>
          </tr>
          <tr>
            <td>Billing Address</td>
            <td>
              <div v-if="billingInfo.stripeCustomer.name">
                {{ billingInfo.stripeCustomer.name }}
              </div>
              <div v-if="billingInfo.stripeCustomer.address.line1">
                {{ billingInfo.stripeCustomer.address.line1 }}
              </div>
              <div v-if="billingInfo.stripeCustomer.address.line2">
                {{ billingInfo.stripeCustomer.address.line2 }}
              </div>
              <div
                v-if="
                  billingInfo.stripeCustomer.address.city ||
                  billingInfo.stripeCustomer.address.state ||
                  billingInfo.stripeCustomer.address.postal_code
                "
              >
                {{
                  [
                    billingInfo.stripeCustomer.address.city,
                    billingInfo.stripeCustomer.address.state,
                    billingInfo.stripeCustomer.address.postal_code,
                  ]
                    .filter(Boolean)
                    .join(", ")
                }}
              </div>
              <div v-if="billingInfo.stripeCustomer.address.country">
                {{
                  selectedCountry(billingInfo.stripeCustomer.address.country)
                    .name
                }}
              </div>
            </td>
          </tr>
          <tr v-if="billingInfo.lastOrder && billingInfo.lastOrder.shipping">
            <td>Last Shipping Address</td>
            <td>
              <div v-if="billingInfo.lastOrder.shipping.name">
                {{ billingInfo.lastOrder.shipping.name }}
              </div>
              <div v-if="billingInfo.lastOrder.shipping.company">
                {{ billingInfo.lastOrder.shipping.company }}
              </div>
              <div v-if="billingInfo.lastOrder.shipping.address">
                {{ billingInfo.lastOrder.shipping.address }}
              </div>
              <div
                v-if="
                  billingInfo.lastOrder.shipping.city ||
                  billingInfo.lastOrder.shipping.state ||
                  billingInfo.lastOrder.shipping.zipCode
                "
              >
                {{
                  [
                    billingInfo.lastOrder.shipping.city,
                    billingInfo.lastOrder.shipping.state,
                    billingInfo.lastOrder.shipping.zipCode,
                  ]
                    .filter(Boolean)
                    .join(", ")
                }}
              </div>
              <div v-if="billingInfo.lastOrder.shipping.country">
                {{
                  selectedCountry(billingInfo.lastOrder.shipping.country).name
                }}
              </div>
            </td>
          </tr>
          <tr>
            <td>Tax Exempt</td>
            <td>
              {{ billingInfo.stripeCustomer.tax_exempt }}
            </td>
          </tr>
          <tr>
            <td>Invoice Prefix</td>
            <td>{{ billingInfo.stripeCustomer.invoice_prefix }}</td>
          </tr>
        </tbody>
        <div v-else class="text-center py-8">No payment method found</div>
      </template>
    </v-simple-table>
    <success-snackbar v-model="showSuccessMessage" :message="successMessage" />
  </v-card>
</template>
<script>
import { mapGetters } from "vuex";
import { API } from "../../api";
import { writeToClipboard } from "../../utils/copyText";
import countryList from "../../utils/countryList.js";
import SuccessSnackbar from "../Base/SuccessSnackbar.vue";

export default {
  name: "CompanyPaymentMethod",
  components: {
    SuccessSnackbar,
  },
  props: {
    id: {
      default: null,
      type: String,
    },
  },
  data() {
    return {
      countryList,
      billingInfo: null,
      loading: false,
      stripeDashboardUrl: null,
      showSuccessMessage: false,
      successMessage: "",
    };
  },
  computed: {
    ...mapGetters(["isProd"]),
  },
  watch: {
    id: {
      immediate: true,
      async handler(val) {
        if (val) {
          this.loading = true;
          this.billingInfo = null;
          try {
            const billingInfo = await API.getBillingInfo(this.id);
            this.billingInfo = billingInfo;
            this.stripeDashboardUrl = this.isProd
              ? `https://dashboard.stripe.com/customers/${billingInfo.stripeCustomerId}`
              : `https://dashboard.stripe.com/test/customers/${billingInfo.stripeCustomerId}`;
            this.loading = false;
          } catch (error) {
            this.loading = false;
          }
        }
      },
    },
  },
  methods: {
    async copyStripeCustomerId(id) {
      await writeToClipboard(String(id));
      this.successMessage =
        "Stripe Customer ID successfully copied to clipboard!";
      this.showSuccessMessage = true;
    },
    async copyBillingEmail(email) {
      await writeToClipboard(String(email));
      this.successMessage = "Billing Email successfully copied to clipboard!";
      this.showSuccessMessage = true;
    },
    async copyPaypalEmail(email) {
      await writeToClipboard(String(email));
      this.successMessage = "Paypal Email successfully copied to clipboard!";
      this.showSuccessMessage = true;
    },
    selectedCountry(countryCode) {
      return this.countryList.find((country) => country.code === countryCode);
    },
  },
};
</script>
